/**
 * ChatChart — renders an interactive chart the Deal Analyst attached to a reply.
 *
 * Spec (from the backend make_chart tool):
 *   { type: "line"|"bar"|"area", title, xKey, xLabel, series:[{key,label}], data:[{x, ...}] }
 * The data can be raw analysis columns OR series the agent derived server-side
 * (ratios, rolling stats, cross-analysis joins).
 */
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const PALETTE = ["#79589F", "#3DB887", "#E8A735", "#6C63FF", "#D64545"];

const fmtNum = (v) => {
  if (v === null || v === undefined || isNaN(v)) return "";
  const a = Math.abs(v);
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  if (a > 0 && a < 10) return v.toFixed(2);
  return v.toFixed(0);
};

const ChatChart = ({ chart }) => {
  const { type = "line", title, xKey = "x", series = [], data = [] } = chart || {};
  if (!data.length || !series.length) return null;

  const Chart = type === "bar" ? BarChart : type === "area" ? AreaChart : LineChart;

  const renderSeries = (s, i) => {
    const color = PALETTE[i % PALETTE.length];
    if (type === "bar")
      return <Bar key={s.key} dataKey={s.key} name={s.label} fill={color} radius={[3, 3, 0, 0]} />;
    if (type === "area")
      return <Area key={s.key} dataKey={s.key} name={s.label} stroke={color} fill={color} fillOpacity={0.22} strokeWidth={2} />;
    return <Line key={s.key} dataKey={s.key} name={s.label} stroke={color} dot={false} strokeWidth={2} />;
  };

  return (
    <div style={{ border: "1px solid var(--ev-border)", borderRadius: "8px", padding: "0.6rem 0.6rem 0.4rem", backgroundColor: "var(--ev-white)" }}>
      {title && (
        <div style={{ fontWeight: 700, fontSize: "0.78rem", color: "var(--ev-text)", marginBottom: "0.3rem", paddingLeft: "0.2rem" }}>
          {title}
        </div>
      )}
      <div style={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <Chart data={data} margin={{ top: 6, right: 10, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
            <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: "var(--ev-text-muted)" }} minTickGap={28} tickMargin={6} />
            <YAxis tick={{ fontSize: 10, fill: "var(--ev-text-muted)" }} width={46} tickFormatter={fmtNum} />
            <Tooltip
              formatter={(v) => fmtNum(v)}
              contentStyle={{ fontSize: "0.72rem", borderRadius: "6px", border: "1px solid var(--ev-border)", backgroundColor: "var(--ev-white)", color: "var(--ev-text)" }}
              labelStyle={{ color: "var(--ev-text-muted)" }}
            />
            {series.length > 1 && <Legend wrapperStyle={{ fontSize: "0.68rem" }} />}
            {series.map(renderSeries)}
          </Chart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ChatChart;
